import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { useApi, useApplicationState } from 'lib/contexts/ApplicationState';
import router from 'next/router';
import { Job, JobSearchCriteria } from 'lib/types/jobs';
import { JobCard } from 'components/JobCard';
import { Pagination } from 'components/Pagination/BasicPagination';
import { ServiceMeta } from 'server/api/types';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { FeaturedTag } from 'components/FeaturedTag';
import { TeamFeatureButton } from 'components/TeamFeatureButton';
import { FilterCriteria } from './FilterCriteria';

interface Props {
  serverJobs: Job[];
  serverMeta: ServiceMeta;
  serverCriteria: JobSearchCriteria;
}

type LoadingState = 'idle' | 'needToFetch' | 'loading';

export const Jobs = ({ serverJobs, serverMeta, serverCriteria }: Props) => {
  const { isAuthed, currentUser } = useApplicationState();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [jobs, setJobs] = useState<Job[] | null>(serverJobs);
  const [meta, setMeta] = useState<ServiceMeta | undefined>(serverMeta);
  const [criteria, setCriteria] = useState<JobSearchCriteria | undefined>(serverCriteria);
  const [jobsState, setJobsState] = useState<LoadingState>(serverJobs ? 'idle' : 'needToFetch');
  const [featuredJobs, setFeaturedJobs] = useState<Job[]>([]);

  const changeCriteria = (c: JobSearchCriteria) => {
    if (!isEqual(criteria, c)) {
      setJobsState('needToFetch');
      setCriteria(c);
    }
  };

  useEffect(() => {
    const getJobs = async () => {
      setJobsState('loading');
      try {
        const result = await API.getAllJobs(criteria);
        const featureResult = await API.getAllJobs({ ...criteria, onlyFeatured: true });
        setJobs(result.data);
        setMeta(result.meta);
        setFeaturedJobs(featureResult?.data);
        setJobsState('idle');
      } catch (error) {
        enqueueSnackbar(`Error loading data: ${error}`, {
          variant: 'error',
        });
        setJobsState('idle');
      }
    };

    if (isAuthed && criteria) {
      if (jobsState === 'needToFetch') {
        getJobs();
      }
    }
  }, [currentUser, criteria, jobsState, isAuthed]);

  /** Display only when on the first page, with no custom sort,
   *  and only when there are more than 4 results on the page */
  const displayFeaturedJobs = !!(
    featuredJobs.length
    && jobs
    && jobs.length > 4
    && !router.query.sort
    && !router.query.teamId);

  return (
    <>
      <LoadingOverlay open={jobsState === 'loading'} />
      <div className="max-w-screen-xl m-auto py-8 px-4">
        <div className="flex justify-between items-center">
          <h1 className="font-semibold text-2xl mb-4">Search Jobs</h1>
          <TeamFeatureButton onSuccess={() => router.push('/jobs/new')} title="Post a Job" minimumPlan="free" />
        </div>
        <FilterCriteria onChange={changeCriteria} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayFeaturedJobs && featuredJobs?.slice(0, 2).map((j) => (
            <div key={j.id} className="col-span-1 border-paleyellow border-4 rounded-lg">
              <div className="absolute">
                <FeaturedTag
                  title="Gondola for Teams members have their jobs randomly featured at the top of jobs searches where the roles match the search criteria, as well as on the homepage"
                  upgradeTitle="Gondola for Teams members have their jobs randomly featured at the top of jobs searches. Sign up for TEAMS to join them!"
                  styling="absolute -left-0.5 -top-0.5"
                  teamsFeature
                />
              </div>
              <JobCard account={j.account} job={j} />
            </div>
          ))}
          {jobs?.filter((j) => {
            if (displayFeaturedJobs && featuredJobs.length) {
              return j.id !== featuredJobs[0]?.id && j.id !== featuredJobs[1]?.id;
            }
            return j.id;
          }).map((j) => (
            <div key={j.id} className="col-span-1">
              <JobCard account={j.account} job={j} />
            </div>
          ))}
        </div>
        {meta && <Pagination paging={meta.paging} canSeeAllPages />}
      </div>
    </>
  );
};
